<script setup lang="ts">
import type { TabsItem } from '@nuxt/ui'
import { NewOpportunitiesAlertModal } from '#components'

const props = defineProps({
  widget: { type: [Boolean, Object], default: false },
  search: { type: Object, default: {} },
})

const platform = useState('platform')

const utmSource = computed(() => props.widget ? (typeof props.widget === "object" ? props.widget.slug : 'widget') : null)
const utmCampaign = computed(() => props.widget ? (typeof props.widget === "object" ? `${props.widget.slug}_apply` : `widget_${ platform.value.slug }`) : null)

const queryCoordinates = useRouteQuery('coordinates', null)
const queryValue = useRouteQuery('value', null)
const queryActivity = useRouteQuery('activity', null)
const queryDomain = useRouteQuery('domain', null)
const querySearch = useRouteQuery('q', null)
const queryGroup = useRouteQuery('group', null)
const queryTemplate = useRouteQuery('template', null)
const queryMissionType = useRouteQuery('missionType', null)

const newOpportunitiesAlertModalOpen = useRouteQuery('alert', null)

const search = reactive({
  coordinates: queryCoordinates.value,
  value: queryValue.value,
  activity: queryActivity.value,
  domain: queryDomain.value,
  q: querySearch.value,
  group: queryGroup.value,
  template: queryTemplate.value,
  missionType: queryMissionType.value,
})

watch(() => search.coordinates, (val) => queryCoordinates.value = val)
watch(() => search.value, (val) => queryValue.value = val)
watch(() => search.activity, (val) => queryActivity.value = val)
watch(() => search.domain, (val) => queryDomain.value = val)
watch(() => search.q, (val) => querySearch.value = val)
watch(() => search.group, (val) => queryGroup.value = val)
watch(() => search.template, (val) => queryTemplate.value = val)
watch(() => search.missionType, (val) => queryMissionType.value = val)

const missionListDefaultRef = ref<{ count?: number; status?: string } | null>(null)
const missionListPunctualRef = ref<{ count?: number; status?: string } | null>(null)
const missionListRemoteRef = ref<{ count?: number; status?: string } | null>(null)

const tab = ref('default')
const items = ref<TabsItem[]>([
  {
    label: 'À proximité',
    value: 'default',
    icon: 'i-ph-map-pin-duotone',
  },
  {
    label: 'À distance',
    value: 'remote',
    icon: 'i-ph-laptop-duotone',
  }
])

watchEffect(() => {
  if (tab.value === 'remote' && missionListRemoteRef.value?.count === 0 && missionListRemoteRef.value?.status === 'success') {
    tab.value = 'default'
  }
})

const overlay = useOverlay()
const modal = overlay.create(NewOpportunitiesAlertModal)

if (newOpportunitiesAlertModalOpen.value) {
  modal.open()
}

const widgetSearchParams = computed(() => {
  if (utmSource.value) {
    return {
      'utm_source': utmSource.value,
      'utm_campaign': utmCampaign.value,
    }
  }
  return {}
})

if (props.widget) {
  useHead({
    script: [
      {
        src: 'https://cdn.benevolt.fr/embed/iframeResizer.contentWindow.js',
        crossorigin: 'anonymous',
        referrerpolicy: 'no-referrer',
      },
      {
        src: '/open-iframe-resizer.js',
        type: 'module',
      },
    ]
  })
}

function isEmpty(r) {
  return !r?.count && r?.status === 'success'
}

const widgetLinesOption = useRouteQuery('lines', '3', { transform: Number })

const lines = computed(() => props.widget ? widgetLinesOption.value : 3)
const showOnlyPunctual = computed(() => platform.value.settings.mode === 'collecte' || search.missionType === 'punctual')

const ponctualInfiniteScroll = computed(() => {
  return showOnlyPunctual.value || (missionListDefaultRef.value?.count === 0 && missionListDefaultRef.value?.status === 'success')
})
</script>

<template>
  <div class="border-b border-muted relative" :class="!widget ? 'bg-default' : ''">
    <UContainer>
      <MissionSearchForm v-model="search" :widget="!!widget" />
    </UContainer>
    <div class="absolute left-1/2 -translate-x-1/2 translate-y-1/2 bottom-0">
      <UTabs v-model="tab" :items="items" :content="false" v-show="missionListRemoteRef?.count" size="lg" class="max-w-120 border border-muted rounded-lg" />
    </div>
  </div>

  <UContainer :class="!widget ? 'pb-20 pt-5 sm:pt-0' : ''">
    <div v-if="platform.settings?.missionSearchPage?.campaignBanner" class="mt-10">
      <UPageCard v-if="search[platform.settings?.missionSearchPage?.campaignBanner.filterKey] !== platform.settings?.missionSearchPage?.campaignBanner.filterValue"
                 :title="platform.settings?.missionSearchPage?.campaignBanner.title"
                 :description="platform.settings?.missionSearchPage?.campaignBanner.description"
                 orientation="horizontal"
                 :ui="{container: 'lg:flex lg:flex-row'}"
      >
        <UButton trailing-icon="i-ph-caret-right" :label="platform.settings?.missionSearchPage?.campaignBanner.button" @click="search[platform.settings?.missionSearchPage?.campaignBanner.filterKey] = platform.settings?.missionSearchPage?.campaignBanner.filterValue" />
      </UPageCard>
      <UButton v-if="search[platform.settings?.missionSearchPage?.campaignBanner.filterKey] === platform.settings?.missionSearchPage?.campaignBanner.filterValue" trailing-icon="i-ph-x" :label="platform.settings?.missionSearchPage?.campaignBanner.activeButton" @click="search[platform.settings?.missionSearchPage?.campaignBanner.filterKey] = null" />
    </div>
    <!--<div class="sm:float-right sm:mt-6 text-right mt-5">
      <UButton variant="subtle" icon="i-ph-bell-duotone" color="neutral" label="Créer une alerte" @click="widget ? navigateTo({path: '/recherche', query: {...useRoute().query, alert: true, utm_source: utmSource, utm_campaign: utmCampaign}}, {open: {target: '_blank'}}) : modal.open()" />
    </div>-->
    <div v-show="tab === 'default'">
      <div v-show="missionListPunctualRef?.count || showOnlyPunctual">
        <h2 class="font-bold text-2xl pb-4 pt-6">Les missions ponctuelles</h2>
        <MissionSearchItems :search="{...search, filter: 'one-shot', remote: 'with', 'per-page': (showOnlyPunctual ? 3 * lines : 3), ...props.search, ...widgetSearchParams}" :infinite-scroll="!widget && ponctualInfiniteScroll" :newOpportunitiesAlert="!(missionListPunctualRef?.count === 3 && missionListDefaultRef?.count)" :widget="widget" ref="missionListPunctualRef" />
      </div>
      <div v-show="!ponctualInfiniteScroll" v-if="!showOnlyPunctual">
        <h2 class="font-bold text-2xl pb-4 pt-6">Les missions<span v-if="missionListDefaultRef?.count"> régulières</span></h2>
        <MissionSearchItems :search="{...search, filter: 'default', remote: 'without', 'per-page': 3 * lines, ...props.search, ...widgetSearchParams}" :infinite-scroll="!widget" :newOpportunitiesAlert="true" :widget="widget" ref="missionListDefaultRef" />
      </div>
    </div>
    <div v-show="tab === 'remote'" v-if="!showOnlyPunctual">
      <h2 class="font-bold text-2xl pb-4 pt-6">Les missions à distance</h2>
      <MissionSearchItems :search="{...search, remote: 'only', 'per-page': 3 * lines, ...props.search, ...widgetSearchParams}" :infinite-scroll="!widget" :newOpportunitiesAlert="true" :widget="widget" ref="missionListRemoteRef" />
    </div>
    <div v-if="(tab === 'remote' && isEmpty(missionListRemoteRef)) || (tab === 'default' && (showOnlyPunctual || isEmpty(missionListDefaultRef)) && isEmpty(missionListPunctualRef))" class="mx-auto w-full max-w-(--container-3xl) px-4 sm:px-6 lg:px-8 py-25 space-y-4 text-center">
      <p class="text-2xl font-bold text-center">Bientôt de nouvelles missions disponibles</p>
      <p class="text-center">Vous serez informé dès qu'une mission sera publiée</p>
      <UButton size="xl" icon="i-ph-bell-duotone" @click="widget ? navigateTo({path: '/recherche', query: {...useRoute().query, alert: true, utm_source: utmSource, utm_campaign: utmCampaign}}, {open: {target: '_blank'}}) : modal.open()">Rester informé</UButton>
      <USeparator class="py-4"/>
      <p class="text-center">N’hésitez pas à élargir vos critères de recherche<span v-show="missionListRemoteRef?.count && tab === 'default'"> ou regarder les missions disponibles à distance</span></p>
    </div>
    <div v-if="widget" class="mt-10 text-center">
      <p class=" font-semibold mb-2 text-xs">Propulsé par<br>
        <a :href="`https://asso.benevolt.fr${ platform.type == 'network' ? '/association/reseau' : '/collectivite' }?utm_source=${utmSource}&utm_campaign=${ utmCampaign }_footer`" target="_blank" class="font-extrabold text-[#E74249] text-2xl">benevolt</a>
      </p>
    </div>
  </UContainer>
</template>